import React, { useMemo, useEffect } from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { ThemeProvider } from '@mui/material/styles';
import { useMediaQuery } from '@mui/material';
import CssBaseline from '@mui/material/CssBaseline';
import type { PaletteMode } from '@mui/material';

import './services/polService';
import App from './App';
import { useAuthStore, useSettingsStore } from './stores';
import { NotificationSnackbar } from './components/common/NotificationSnackbar';
import { createAppTheme, ThemeIntensity } from './theme';
import './loading.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

// Theme wrapper that responds to settings store
const ThemedApp: React.FC = () => {
  const { settings } = useSettingsStore();
  const checkAuth = useAuthStore((s) => s.checkAuth);
  const prefersDarkMode = useMediaQuery('(prefers-color-scheme: dark)');
  
  // Initialize auth on mount
  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const theme = useMemo(() => {
    let mode: PaletteMode;
    let intensity: ThemeIntensity;
    
    if (settings.appearance.themeMode === 'system') {
      // System mode: use OS preference
      mode = prefersDarkMode ? 'dark' : 'light';
      intensity = mode;
    } else if (settings.appearance.themeMode === 'soft-light') {
      // Soft light uses 'light' palette but with soft-light intensity
      mode = 'light';
      intensity = 'soft-light';
    } else if (settings.appearance.themeMode === 'soft-dark') {
      // Soft dark uses 'dark' palette but with soft-dark intensity
      mode = 'dark';
      intensity = 'soft-dark';
    } else {
      // Standard light or dark
      mode = settings.appearance.themeMode as PaletteMode;
      intensity = settings.appearance.themeMode as ThemeIntensity;
    }
    
    // Pass custom colors when "custom" scheme is selected
    const customColors = settings.appearance.colorScheme === 'custom' 
      ? {
          primary: settings.appearance.customPrimaryColor,
          secondary: settings.appearance.customSecondaryColor,
        }
      : undefined;

    return createAppTheme(mode, settings.appearance.colorScheme, customColors, intensity);
  }, [
    settings.appearance.themeMode, 
    settings.appearance.colorScheme, 
    settings.appearance.customPrimaryColor,
    settings.appearance.customSecondaryColor,
    prefersDarkMode
  ]);

  // Set data-theme attribute on body for native date picker styling
  useEffect(() => {
    document.body.setAttribute('data-theme', theme.palette.mode);
  }, [theme.palette.mode]);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <BrowserRouter>
        <App />
        <NotificationSnackbar />
      </BrowserRouter>
    </ThemeProvider>
  );
};

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);

root.render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <ThemedApp />
      <ReactQueryDevtools initialIsOpen={false} />
    </QueryClientProvider>
  </React.StrictMode>
);
